import { useQueryClient } from '@tanstack/react-query';
import { estCommandeFortemix } from '@mosaik/core';
import { useToasts } from '../composants/Toasts';
import { api, ErreurApi } from './client';
import type { CommandeDetaillee } from './types';

/** Commandes dont un relevé enchaîné tourne déjà : un double clic n'en lance pas deux. */
const enCours = new Set<string>();

/** Le relevé qui fait avancer l'étape suivante, `null` s'il n'y en a pas. */
function releveSuivant(c: CommandeDetaillee): 'fortemix' | 'suivi' | null {
  if (c.livre) return null;
  const { expedition, tracking } = c.etapes;
  /*
   * Pas avant l'expédition : une commande tout juste transmise n'est pas encore
   * sur le portail, et l'y chercher la consignerait « absente du portail » —
   * un échec qui réclame de la repasser.
   */
  if (!expedition.fait) return null;
  // Tracking à venir : seul le portail Fortemix le publie.
  if (!tracking.fait) {
    return estCommandeFortemix(c.articles.map((a) => a.reference), c.fournisseurs) ? 'fortemix' : null;
  }
  // Tracking connu : reste la livraison, que dit le transporteur.
  return c.tracking ? 'suivi' : null;
}

/**
 * Lance, sitôt une étape validée, le relevé de l'étape suivante.
 *
 * Un mail d'expédition parti n'attend plus le passage de 8 h 05 pour aller
 * chercher le tracking chez Fortemix, ni un tracking annoncé celui de 8 h pour
 * interroger le transporteur. Le relevé tourne en fond, la fenêtre se ferme
 * sans l'attendre ; seule une découverte, ou un échec, se dit en toast. Les
 * règles sont celles des boutons « Relever » de la fiche — c'est le même appel.
 */
export function useReleveEtapeSuivante() {
  const client = useQueryClient();
  const { ajouter } = useToasts();

  return (commande: CommandeDetaillee) => {
    const releve = releveSuivant(commande);
    if (!releve || enCours.has(commande.id)) return;
    enCours.add(commande.id);

    const fin = () => {
      enCours.delete(commande.id);
      void client.invalidateQueries({ queryKey: ['commandes'] });
      void client.invalidateQueries({ queryKey: ['journal'] });
      void client.invalidateQueries({ queryKey: ['commande', commande.id] });
    };

    const appel = releve === 'fortemix'
      ? api.post<{ trackingTrouve: boolean; commande: CommandeDetaillee }>(
        `/api/commandes/${commande.id}/fortemix/relever`, {},
      ).then((r) => {
        if (!r.trackingTrouve) return;
        // La découverte part seule chez le client : on dit si le mail est parti.
        if (r.commande.envois.tracking.etat === 'envoye') {
          ajouter(`Fortemix : tracking relevé pour ${commande.ref}, mail envoyé au client.`);
        } else {
          ajouter(`Fortemix : tracking relevé pour ${commande.ref}, mais le mail au client n’est pas parti.`, 'bad');
        }
      })
      : api.post<CommandeDetaillee>(`/api/commandes/${commande.id}/suivi/relever`, {})
        .then((r) => {
          if (r.releveErreur) ajouter(`Suivi de ${commande.ref} : ${r.releveErreur}`, 'bad');
          else if (r.statutTransporteur && r.statutTransporteur !== commande.statutTransporteur) {
            ajouter(`Suivi de ${commande.ref} : ${r.statutTransporteur}.`);
          }
        });

    appel
      .catch((e) => ajouter(
        `Relevé ${releve === 'fortemix' ? 'Fortemix' : 'du suivi'} de ${commande.ref} : `
        + (e instanceof ErreurApi ? e.message : 'échec.'),
        'bad',
      ))
      .finally(fin);
  };
}
